import {
  convertDateToYYYYMMDDFormat,
  getFirstDayOfMonth,
  getToday,
} from '@repo/ui';
import { useFormik } from 'formik';
import { useAuth } from 'presentation/app/hooks/useAuth';
import { ChangeEvent, useEffect, useMemo, useState } from 'react';
import * as Yup from 'yup';

import { useProfessionalReport } from './useProfessionalReport';

const DEFAULT_ROWS_PER_PAGE = 10;
const FIRST_API_PAGE = 1;

export const usePendingActivitiesReport = () => {
  const { currentUser } = useAuth();
  const isManager = Number(currentUser?.activeProfile?.profileId) === 2;

  const {
    statusGet,
    statusGetSummary,
    statusExport,
    organizations,
    courses,
    semesters,
    curricularUnits,
    groups,
    professionals,
    screenData,
    kpis,
    requestGetAllOrganizationsUser,
    requestGetAllCoursesUser,
    requestGetAllSemestersUser,
    requestGetAllCurricularUnitsUser,
    requestGetAllGroupsUser,
    requestGetAllProfessionalsSelectOptions,
    requestActivitiesPendingScreen,
    exportPendingActivities,
  } = useProfessionalReport();

  const [selectedPage, setSelectedPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(DEFAULT_ROWS_PER_PAGE);

  const validation = useFormik({
    enableReinitialize: true,
    initialValues: {
      entity: null as { label: string; value: string } | null,
      course: null as { label: string; value: string } | null,
      startDate: convertDateToYYYYMMDDFormat(getFirstDayOfMonth()),
      endDate: convertDateToYYYYMMDDFormat(getToday()),
      semester: null as { label: string; value: string } | null,
      unit: null as { label: string; value: string } | null,
      group: null as { label: string; value: string } | null,
      professional: null as { label: string; value: string } | null,
    },
    validationSchema: Yup.object({
      entity: Yup.object().nullable(),
      course: Yup.object().nullable(),
      semester: Yup.object().nullable(),
      unit: Yup.object().nullable(),
      group: Yup.object().nullable(),
      professional: Yup.object().nullable(),
      startDate: Yup.date().required('Data inicial é obrigatória.'),
      endDate: Yup.date().required('Data final é obrigatória.'),
    }),
    onSubmit: (values) => {
      setSelectedPage(0);
      requestActivitiesPendingScreen(makeRequestParams(values, FIRST_API_PAGE));
    },
  });

  const makeFilterParams = (values: typeof validation.values) => ({
    startDate: values.startDate,
    endDate: values.endDate,
    entityHashId: values.entity?.value || undefined,
    courseHashId: values.course?.value || undefined,
    semesterHashId: values.semester?.value || undefined,
    curricularUnitHashId: values.unit?.value || undefined,
    groupHashId: values.group?.value || undefined,
    professionalHashId: values.professional?.value || undefined,
  });

  const makeRequestParams = (
    values: typeof validation.values,
    page: number,
    length: number = rowsPerPage,
  ) => ({
    ...makeFilterParams(values),
    page,
    length,
  });

  const dataSource = screenData;
  const kpiSource = kpis;
  const hasDataSource = (dataSource?.data?.length || 0) > 0;
  const totalItems = dataSource?.total || 0;
  const totalPages = Math.max(1, Math.ceil(totalItems / rowsPerPage));

  const pages = useMemo(
    () => Array.from({ length: totalPages }, (_, index) => index),
    [totalPages],
  );

  const handlePageChange = (page: number) => {
    if (page < 0 || page >= totalPages) return;

    setSelectedPage(page);
    requestActivitiesPendingScreen(
      makeRequestParams(validation.values, page + FIRST_API_PAGE, rowsPerPage),
      { shouldFetchSummary: false },
    );
  };

  const handleRowsPerPageChange = (event: ChangeEvent<HTMLInputElement>) => {
    const newRowsPerPage = Number(event.target.value);

    setRowsPerPage(newRowsPerPage);
    setSelectedPage(0);

    requestActivitiesPendingScreen(
      makeRequestParams(validation.values, FIRST_API_PAGE, newRowsPerPage),
      { shouldFetchSummary: false },
    );
  };

  const handleExport = (reportFormat: '0' | '1') => {
    exportPendingActivities(reportFormat, makeFilterParams(validation.values));
  };

  useEffect(() => {
    requestGetAllCoursesUser();
    requestGetAllSemestersUser('');
    requestActivitiesPendingScreen(
      makeRequestParams(validation.values, FIRST_API_PAGE),
    );

    if (isManager) {
      requestGetAllOrganizationsUser();
    }
  }, []);

  useEffect(() => {
    requestGetAllCoursesUser(validation.values.entity?.value);

    validation.setFieldValue('course', null);
    validation.setFieldValue('unit', null);
    validation.setFieldValue('group', null);
    validation.setFieldValue('professional', null);
  }, [validation.values.entity?.value]);

  useEffect(() => {
    requestGetAllCurricularUnitsUser({
      entityId: validation.values.entity?.value,
      courseId: validation.values.course?.value,
    });

    requestGetAllGroupsUser({
      entityId: validation.values.entity?.value,
      courseId: validation.values.course?.value,
      semesterId: validation.values.semester?.value,
      curricularUnitId: validation.values.unit?.value,
    });

    requestGetAllProfessionalsSelectOptions({
      entity: validation.values.entity?.value,
      course: validation.values.course?.value,
      semester: validation.values.semester?.value,
      curricularUnit: validation.values.unit?.value,
      groups: validation.values.group?.value
        ? [validation.values.group.value]
        : [],
    });
  }, [
    validation.values.entity?.value,
    validation.values.course?.value,
    validation.values.semester?.value,
    validation.values.unit?.value,
    validation.values.group?.value,
  ]);

  return {
    isManager,
    validation,
    statusGet,
    statusGetSummary,
    statusExport,
    organizations,
    courses,
    semesters,
    curricularUnits,
    groups,
    professionals,
    handleExport,
    kpiSource,
    dataSource,
    hasDataSource,
    totalItems,
    selectedPage,
    totalPages,
    pages,
    rowsPerPage,
    handlePageChange,
    handleRowsPerPageChange,
  };
};
